import React from 'react';
import { style } from 'typestyle';
import { SaveAndRevertButtons } from './SaveAndRevertButtons';
import { SettingsCoordinatorContext } from './SettingsCoordinatorContexts';
import { useSettingsCoordinatorStateInternal } from './SettingsCoordinatorHook';
import { SectionContainerBaseProps, SettingsSectionList } from './SettingsSectionList';

export type SettingsCoordinatorProps<T> = T
  & Partial<Pick<SectionContainerBaseProps<any>, 'sections'>>
  & {
  /**
   * Handler for when all child forms have submitted successfully
   */
  readonly onSubmit?: () => Promise<unknown>;
  /**
   * Handler for when any form has failed to submit
   */
  readonly onSubmitFailure?: (error: any) => Promise<void>;
  /**
   * Handler for when reset to default has succeeded. Only used by server settings so far
   */
  readonly onResetToDefaultSuccess?: () => Promise<void>;
  /**
   * Handler for when reset to default has failed. Only used by server settings so far
   */
  readonly onResetToDefaultFailure?: () => Promise<void>;
  /**
   * Specify whether to show or hide the reset button at the bottom of the settings page
   */
  readonly showResetToDefaultButton?: boolean;
};

/**
 * A reusable settings coordinator component with save and revert buttons. It provides supports for descendant
 * components to register callbacks to update their dirty, valid, and submitting states, and to handle submit and
 * revert clicks from this component. Currently, Formik forms are directly supported. Use the
 * {@link useSettingsCoordinator} hook to wire a Formik form nested at any level within a SettingsCoordinator
 * @param props
 * @constructor
 */
export function SettingsCoordinator<Props>(props: React.PropsWithChildren<SettingsCoordinatorProps<Props>>): JSX.Element {
  const {dispatch, triggerResetDefault, ...saveRevertProps} = useSettingsCoordinatorStateInternal(
    props.onSubmit,
    props.onSubmitFailure,
    props.onResetToDefaultSuccess,
    props.onResetToDefaultFailure
  );

  return <>
    <SaveAndRevertButtons {...saveRevertProps}/>
    <SettingsCoordinatorContext.Provider value={dispatch}>
      <div className={formStyle}>
        {props.children && <div className={sectionListStyle}>
          {props.children}
        </div>}
        {props.sections?.length && <SettingsSectionList
          sections={props.sections}
          settings={props}
          formStyle={sectionListStyle}
        />}
        <SaveAndRevertButtons {...saveRevertProps} triggerResetDefault={props.showResetToDefaultButton ? triggerResetDefault : null}/>
      </div>
    </SettingsCoordinatorContext.Provider>
  </>;
}

const formStyle = style({
  flex: 1,
  paddingTop: 30,
  overflow: 'scroll',
});

const sectionListStyle = style({
  width: 508, // should verify where these values came from
  marginLeft: 85,
});
